/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import { KELVIN } from '../Soundings/constants';
import type { ScalesType } from '../Soundings/types';

const CP = 1.03e3;
const L_CONST = 2.5e6;
const RD = 287.0;
const RV = 461.0;

const moistAdiabaticLapserate = (
  pressure: number,
  temperature: number,
  dp: number,
): [number, number] => {
  const kelvin = temperature + KELVIN;
  const lsbc = (L_CONST / RV) * (1.0 / KELVIN - 1.0 / kelvin);
  const rw = 6.11 * Math.exp(lsbc) * (0.622 / pressure);
  const lrwbt = (L_CONST * rw) / (RD * kelvin);
  const nume = ((RD * kelvin) / (CP * pressure)) * (1.0 + lrwbt);
  const deno = 1.0 + lrwbt * ((0.622 * L_CONST) / (CP * kelvin));
  const gradi = nume / deno;
  const dt = dp * gradi;

  return [dp, dt];
};

interface DrawMoistAdiabatsProps {
  staticPressureArray: number[];
  minTemperature: number;
  maxTemperature: number;
  minPressure: number;
  maxPressure: number;
  width: number;
  height: number;
  dp: number;
  xScale: ScalesType;
  yScale: ScalesType;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  diagramType: SoundingDiagramType;
  d3GraphId: string;
}

export const drawMoistAdiabats = ({
  staticPressureArray,
  minTemperature,
  maxTemperature,
  minPressure,
  maxPressure,
  width,
  height,
  xScale,
  yScale,
  svg,
  dp,
  diagramType,
  d3GraphId,
}: DrawMoistAdiabatsProps): void => {
  /* We need the moist adiabat to cross the isotherm at 1000hPa
     Therefore we start the calculations from 1000hPa and work upwards (towards 0)
     then draw a separate line starting from 1000hPa working down (towards defined maxPressure) */

  for (let Tbase = -40; Tbase < 240; Tbase += 10) {
    let Pnew = 1000;
    let Tnew = Tbase;
    const Px: [number, number][] = [];

    // First calculate position of moist adiabat at a pressure of 1000hPa
    const Temp = Tnew + -1 * minTemperature;
    const Tpx1000 = (width * Temp) / (maxTemperature - minTemperature);
    const Ppx1000 =
      (height * (Math.log(Pnew) - Math.log(minPressure))) /
      (Math.log(maxPressure) - Math.log(minPressure));

    Px.push([Tpx1000, Ppx1000]);

    // Then calculate the rest of the moist adiabat for pressures <1000hPa
    for (const pressure of staticPressureArray) {
      if (pressure < 1000) {
        const DPDT = moistAdiabaticLapserate(Pnew, Tnew, dp);
        Tnew += DPDT[1];
        Pnew += DPDT[0];

        Px.push([xScale(Tnew), yScale(pressure)]);
      }
    }
    if (diagramType === 'SKEWT') {
      for (const PxElement of Px) {
        PxElement[0] = PxElement[0] + height - PxElement[1];
      }
    }
    const lineGenerator = d3.line();
    const maPathString = lineGenerator(Px as [number, number][]);

    // Draw this first section of the line
    svg
      .append('path')
      .attr('d', maPathString)
      .style('fill', 'none')
      .style('stroke-width', 1)
      .style('stroke', 'purple')
      .style('stroke-opacity', 0.5)
      .attr('clip-path', `url(#clip-${d3GraphId}-${diagramType}d3)`);

    // Now work backwards from 1000hPa to calculate most adiabat until the bottom of the plot
    const PxBottom: [number, number][] = [];
    PxBottom.push([Tpx1000, Ppx1000]);
    let PnewBottom = 1000;
    let TnewBottom = Tbase;

    const startIndex = staticPressureArray.findIndex((value) => value === 1000);

    for (let i = startIndex - 1; i >= 0; i -= 1) {
      const DPDT = moistAdiabaticLapserate(PnewBottom, TnewBottom, -dp);
      TnewBottom += DPDT[1];
      PnewBottom += DPDT[0];

      PxBottom.push([xScale(TnewBottom), yScale(staticPressureArray[i])]);
    }

    if (diagramType === 'SKEWT') {
      // Skew/shear transformation for skew-t
      for (const PxElement of PxBottom) {
        PxElement[0] = PxElement[0] + height - PxElement[1];
      }
    }

    const maPathStringBottom = lineGenerator(PxBottom as [number, number][]);

    svg
      .append('path')
      .attr('d', maPathStringBottom)
      .style('fill', 'none')
      .style('stroke-width', 1)
      .style('stroke', 'purple')
      .style('stroke-opacity', 0.5)
      .attr('clip-path', `url(#clip-${d3GraphId}-${diagramType}d3)`);
  }
};

interface DrawMoistAdiabatsBijvoetProps {
  minPressure: number;
  maxPressure: number;
  xScale: ScalesType;
  yScale: ScalesType;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  d3GraphId: string;
}
export const drawMoistAdiabatsBijvoet = ({
  minPressure,
  maxPressure,
  xScale,
  yScale,
  svg,
  d3GraphId,
}: DrawMoistAdiabatsBijvoetProps): void => {
  for (let Tbase = -20; Tbase < 40; Tbase += 5) {
    const theta = Tbase + KELVIN;
    const Px = [];
    const pressureBounds = [minPressure, maxPressure];

    for (const bound of pressureBounds) {
      Px.push([xScale(theta), yScale(bound)]);
    }

    const lineGenerator = d3.line();
    const isoPathString = lineGenerator(Px as [number, number][]);
    const strID = `${Tbase}-moistadiabat`;
    svg
      .append('path')
      .attr('id', strID)
      .attr('d', isoPathString)
      .style('fill', 'none')
      .style('stroke-width', 1)
      .style('stroke', 'purple')
      .style('stroke-opacity', 0.5)
      .attr('clip-path', `url(#clip-${d3GraphId}-BIJVOETd3)`);
  }
};
